import React from "react";
import { useParams, Link } from "react-router-dom";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Button } from "../components/ui/button";
import {
  Trophy,
  Calendar,
  Users,
  Target,
  TrendingUp,
  Medal,
  ArrowRight,
} from "lucide-react";

// Mock data - replace with actual API calls
const mockLeague = {
  id: "1",
  name: "757 City Championship",
  description:
    "The premier Magic: The Gathering league for the Hampton Roads area. Compete across multiple formats and stores to qualify for the season championship.",
  region: "Hampton Roads (757)",
  season: "Season 1",
  startDate: "2024-01-01",
  endDate: "2024-03-31",
  isActive: true,
  maxPlayers: 200,
  totalPlayers: 156,
  totalEvents: 12,
  completedEvents: 8,
};

const mockTopStandings = [
  {
    rank: 1,
    playerName: "John Doe",
    points: 45,
    events: 8,
    wins: 24,
    losses: 8,
  },
  {
    rank: 2,
    playerName: "Jane Smith",
    points: 42,
    events: 7,
    wins: 22,
    losses: 9,
  },
  {
    rank: 3,
    playerName: "Mike Johnson",
    points: 39,
    events: 8,
    wins: 20,
    losses: 12,
  },
  {
    rank: 4,
    playerName: "Sarah Wilson",
    points: 36,
    events: 6,
    wins: 18,
    losses: 6,
  },
  {
    rank: 5,
    playerName: "Tom Brown",
    points: 33,
    events: 7,
    wins: 17,
    losses: 11,
  },
];

const mockRecentEvents = [
  {
    id: "1",
    name: "Standard Showdown",
    date: "2024-01-10",
    format: "Standard",
    participants: 24,
    winner: "John Doe",
  },
  {
    id: "2",
    name: "Modern Masters",
    date: "2024-01-08",
    format: "Modern",
    participants: 18,
    winner: "Jane Smith",
  },
  {
    id: "3",
    name: "Draft Night",
    date: "2024-01-05",
    format: "Draft",
    participants: 16,
    winner: "Mike Johnson",
  },
];

const mockUpcomingEvents = [
  {
    id: "4",
    name: "Standard Showdown",
    date: "2024-01-15",
    format: "Standard",
    location: "Game Store A",
  },
  {
    id: "5",
    name: "Modern Masters",
    date: "2024-01-20",
    format: "Modern",
    location: "Game Store B",
  },
  {
    id: "6",
    name: "Draft Night",
    date: "2024-01-25",
    format: "Draft",
    location: "Game Store C",
  },
];

const LeagueOverview: React.FC = () => {
  const { leagueId } = useParams<{ leagueId: string }>();

  const getRankIcon = (rank: number) => {
    switch (rank) {
      case 1:
        return "🥇";
      case 2:
        return "🥈";
      case 3:
        return "🥉";
      default:
        return null;
    }
  };

  const getTop8Status = (rank: number) => {
    if (rank <= 8) {
      return (
        <div className="flex items-center gap-1 text-xs text-green-600 dark:text-green-400">
          <Medal className="h-3 w-3" />
          <span>Top 8</span>
        </div>
      );
    }
    return null;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
            <Trophy className="h-8 w-8 text-primary" />
            {mockLeague.name}
          </h1>
          <p className="text-muted-foreground">{mockLeague.description}</p>
        </div>
        <div className="flex gap-2 mt-4 sm:mt-0">
          <Button asChild>
            <Link to={`/league/${leagueId}/standings`}>View Standings</Link>
          </Button>
          <Button variant="outline" asChild>
            <Link to={`/league/${leagueId}/events`}>Browse Events</Link>
          </Button>
        </div>
      </div>

      {/* League Stats */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Players</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{mockLeague.totalPlayers}</div>
            <p className="text-xs text-muted-foreground">
              of {mockLeague.maxPlayers} max
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Events</CardTitle>
            <Calendar className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{mockLeague.totalEvents}</div>
            <p className="text-xs text-muted-foreground">
              {mockLeague.completedEvents} completed
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Season Progress
            </CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {Math.round(
                (mockLeague.completedEvents / mockLeague.totalEvents) * 100
              )}
              %
            </div>
            <p className="text-xs text-muted-foreground">
              {mockLeague.completedEvents} of {mockLeague.totalEvents} events
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Championship</CardTitle>
            <Target className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">Top 8</div>
            <p className="text-xs text-muted-foreground">Qualify for finals</p>
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        {/* Current Standings Preview */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span>Current Standings</span>
              <Button variant="ghost" size="sm" asChild>
                <Link to={`/league/${leagueId}/standings`}>
                  View All
                  <ArrowRight className="h-4 w-4 ml-1" />
                </Link>
              </Button>
            </CardTitle>
            <CardDescription>Top 5 players in the league</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {mockTopStandings.map((player) => (
                <div
                  key={player.rank}
                  className="flex items-center justify-between p-3 rounded-lg border"
                >
                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold">{player.rank}</span>
                      {getRankIcon(player.rank) && (
                        <span className="text-lg">
                          {getRankIcon(player.rank)}
                        </span>
                      )}
                    </div>
                    <div>
                      <p className="font-medium">{player.playerName}</p>
                      <p className="text-sm text-muted-foreground">
                        {player.events} events • {player.wins}W-{player.losses}L
                      </p>
                      {getTop8Status(player.rank)}
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold">{player.points} pts</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Recent Events */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span>Recent Events</span>
              <Button variant="ghost" size="sm" asChild>
                <Link to={`/league/${leagueId}/events`}>
                  View All
                  <ArrowRight className="h-4 w-4 ml-1" />
                </Link>
              </Button>
            </CardTitle>
            <CardDescription>Latest tournament results</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {mockRecentEvents.map((event) => (
                <div
                  key={event.id}
                  className="flex items-center justify-between p-3 rounded-lg border"
                >
                  <div>
                    <p className="font-medium text-foreground">{event.name}</p>
                    <p className="text-sm text-muted-foreground">
                      {event.format} • {event.participants} participants
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {formatDate(event.date)}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-medium">Winner</p>
                    <p className="text-sm text-muted-foreground">
                      {event.winner}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Upcoming Events */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>Upcoming Events</span>
            <Button variant="ghost" size="sm" asChild>
              <Link to={`/league/${leagueId}/events`}>
                View All
                <ArrowRight className="h-4 w-4 ml-1" />
              </Link>
            </Button>
          </CardTitle>
          <CardDescription>Next scheduled tournaments</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-3">
            {mockUpcomingEvents.map((event) => (
              <div
                key={event.id}
                className="p-4 border rounded-lg hover:bg-muted/50 transition-colors"
              >
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-lg">
                    {event.format === "Standard"
                      ? "⚔️"
                      : event.format === "Modern"
                        ? "🔥"
                        : event.format === "Draft"
                          ? "📦"
                          : "🎮"}
                  </span>
                  <h3 className="font-medium text-foreground">{event.name}</h3>
                </div>
                <p className="text-sm text-muted-foreground mb-2">
                  {event.format} • {event.location}
                </p>
                <p className="text-sm font-medium">{formatDate(event.date)}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* League Information */}
      <Card>
        <CardHeader>
          <CardTitle>League Information</CardTitle>
          <CardDescription>Season details and important dates</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-4">
              <div>
                <h4 className="font-semibold mb-2">Season Details</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Season:</span>
                    <span>{mockLeague.season}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Region:</span>
                    <span>{mockLeague.region}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Start Date:</span>
                    <span>{formatDate(mockLeague.startDate)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">End Date:</span>
                    <span>{formatDate(mockLeague.endDate)}</span>
                  </div>
                </div>
              </div>
            </div>
            <div className="space-y-4">
              <div>
                <h4 className="font-semibold mb-2">Scoring System</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">
                      Participation:
                    </span>
                    <span>+1 point per event</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Match Win:</span>
                    <span>+3 points</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Match Draw:</span>
                    <span>+1 point</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Top 8 Bonus:</span>
                    <span>1st +10, 2nd +6, etc.</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default LeagueOverview;
